import { useState, useEffect, useCallback } from 'react';

// Mobile breakpoint constant - consistent across all components
export const MOBILE_BREAKPOINT = 768;

// Breakpoint values (consistent with TailwindCSS)
const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

/**
 * Custom hook to match media queries
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Set initial value
    setMatches(mediaQuery.matches);

    // Add listener
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * Hook for mobile device detection and viewport utilities
 * Returns responsive breakpoint information for mobile-first design
 */
export function useMobile() {
  return {
    // Breakpoint detection
    isMobile: useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`),
    isTablet: useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`),
    isDesktop: useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`),
    isSmallMobile: useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`),
    isLargeMobile: useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`),

    // Specific checks
    isExtraSmall: useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`),
    isSmall: useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`),
    isMedium: useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`),
    isLarge: useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px) and (max-width: ${BREAKPOINTS.xl - 1}px)`),
    isExtraLarge: useMediaQuery(`(min-width: ${BREAKPOINTS.xl}px)`),
  };
}

/**
 * Hook for viewport dimensions and mobile detection with auto-update on resize.
 * Use this instead of manual window.innerWidth checks for consistent behavior.
 *
 * Features:
 * - Auto-updates on window resize (debounced for performance)
 * - SSR-safe with sensible defaults
 * - Provides both boolean mobile detection and exact dimensions
 * - Uses consistent MOBILE_BREAKPOINT (768px) across all components
 */
export function useViewport() {
  const [viewport, setViewport] = useState(() => {
    if (typeof window === 'undefined') {
      return { width: 1024, height: 768, isMobile: false };
    }
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      isMobile: window.innerWidth <= MOBILE_BREAKPOINT,
    };
  });

  const updateViewport = useCallback(() => {
    setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
      isMobile: window.innerWidth <= MOBILE_BREAKPOINT,
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Update immediately on mount
    updateViewport();

    // Debounced resize handler for performance
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateViewport, 100);
    };

    window.addEventListener('resize', handleResize);

    // Also listen to orientation changes for mobile devices
    window.addEventListener('orientationchange', updateViewport);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, [updateViewport]);

  return viewport;
}

/**
 * Get viewport height accounting for mobile browser chrome
 * Uses dynamic viewport height (dvh) when available
 */
export function getViewportHeight(): number {
  // Use visualViewport if available (accounts for mobile keyboard, browser chrome)
  if (window.visualViewport) {
    return window.visualViewport.height;
  }

  return window.innerHeight;
}

/**
 * Check if device has touch capability
 */
export function isTouchDevice(): boolean {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-expect-error - some browsers don't have this
    navigator.msMaxTouchPoints > 0
  );
}

/**
 * Get safe area insets for notched devices (iPhone X+, etc.)
 * Returns insets in pixels
 */
export function getSafeAreaInsets() {
  const style = getComputedStyle(document.documentElement);

  return {
    top: parseInt(style.getPropertyValue('--sat') || '0', 10),
    bottom: parseInt(style.getPropertyValue('--sab') || '0', 10),
    left: parseInt(style.getPropertyValue('--sal') || '0', 10),
    right: parseInt(style.getPropertyValue('--sar') || '0', 10),
  };
}

/**
 * Hook for safe area insets
 */
export function useSafeAreaInsets() {
  return getSafeAreaInsets();
}

// Re-export useMediaQuery for direct usage
export { useMediaQuery };
